import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common';
import { httpClient } from '@/infrastructure/http';

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
};

type AiStatus = {
  enabled: boolean;
  model: string | null;
  readOnly: boolean;
};

const EXAMPLE_KEYS = [
  'exampleDebtors',
  'exampleUnpaid',
  'exampleEmails',
  'exampleMetrics',
] as const;

const USE_KEYS = ['useCollections', 'usePayments', 'useUsage', 'useMetrics'] as const;

const OPEN_STORAGE_KEY = 'coofit.assistant.open';
export const COOFITO_ICON = '/assets/coofito-icon.png';

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const CoofitoAvatar = ({ className = 'h-7 w-7' }: { className?: string }) => (
  <img
    src={COOFITO_ICON}
    alt=""
    className={`rounded-full object-cover shadow-sm ring-1 ring-black/5 ${className}`}
    draggable={false}
  />
);

type Props = {
  collapsed?: boolean;
  panelSide?: 'right' | 'inside';
};

export const SidebarAssistantChat: React.FC<Props> = ({
  collapsed = false,
  panelSide = 'right',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === '1';
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const copiedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    window.localStorage.setItem(OPEN_STORAGE_KEY, open ? '1' : '0');
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || !rootRef.current) return;
      if (!rootRef.current.contains(target)) {
        setHelpOpen(false);
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await httpClient.get<AiStatus>('/ai/status');
        if (!cancelled) {
          setStatus(res.data);
          setStatusError(null);
        }
      } catch {
        if (!cancelled) {
          setStatusError(t('pages.assistant.statusError'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!open || helpOpen) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending, open, helpOpen]);

  useEffect(() => {
    if (open && !helpOpen) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(id);
    }
  }, [open, helpOpen]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const copyMessage = useCallback(async (id: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopiedId(null), 1800);
    } catch {
      // ignore clipboard errors
    }
  }, []);

  const sendMessage = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || sending) return;
      if (status && !status.enabled) return;

      setHelpOpen(false);
      setSendError(null);
      const userMsg: ChatMessage = { id: newId(), role: 'user', content: text };
      const nextHistory = [...messages, userMsg];
      setMessages(nextHistory);
      setInput('');
      setSending(true);

      try {
        const payload = nextHistory.map(({ role, content }) => ({ role, content }));
        const res = await httpClient.post<{ reply: string }>(
          '/ai/chat',
          { messages: payload },
          { timeout: 60000 }
        );
        setMessages((prev) => [
          ...prev,
          { id: newId(), role: 'assistant', content: res.data.reply },
        ]);
      } catch (err: any) {
        const apiMsg = err?.response?.data?.message;
        setSendError(apiMsg || t('pages.assistant.sendError'));
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        setInput(text);
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
    },
    [messages, sending, status, t]
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const disabled = sending || status?.enabled === false;

  const panelClass =
    panelSide === 'inside'
      ? 'absolute bottom-[calc(100%+0.5rem)] left-2 right-2 z-50 flex h-[min(65vh,520px)] md:h-[min(60vh,560px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 dark:border-gray-700 dark:bg-gray-900'
      : 'absolute bottom-3 left-[calc(100%+0.75rem)] z-50 flex h-[min(70vh,560px)] w-[min(calc(100vw-5rem),380px)] md:w-[min(calc(100vw-15rem),420px)] lg:w-[min(calc(100vw-16rem),440px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 dark:border-gray-700 dark:bg-gray-900';

  return (
    <div
      ref={rootRef}
      className="relative flex-shrink-0 border-t border-slate-800 bg-slate-900 p-2 md:p-2.5 panel-safe-bottom"
    >
      {open && (
        <div className={panelClass} role="dialog" aria-label={t('pages.assistant.title')}>
          <div className="flex flex-shrink-0 items-start justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3.5 py-3 dark:border-gray-800 dark:bg-gray-950">
            <div className="flex min-w-0 items-center gap-2.5">
              <CoofitoAvatar className="h-8 w-8" />
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                {t('pages.assistant.title')}
              </h2>
            </div>
            <div className="flex flex-shrink-0 items-center gap-0.5">
              <button
                type="button"
                onClick={() => setHelpOpen((v) => !v)}
                className={`rounded-lg px-2 py-1.5 text-xs font-medium transition ${
                  helpOpen
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800 dark:hover:bg-gray-800 dark:hover:text-slate-200'
                }`}
                aria-pressed={helpOpen}
                title={t('pages.assistant.help')}
              >
                {t('pages.assistant.help')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setHelpOpen(false);
                  setOpen(false);
                }}
                className="panel-touch rounded-xl text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-gray-800 dark:hover:text-slate-200"
                aria-label={t('pages.assistant.close')}
                title={t('pages.assistant.close')}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {(statusError || status?.enabled === false) && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
              {statusError || t('pages.assistant.disabled')}
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col">
            {helpOpen ? (
              <div className="flex-1 space-y-4 overflow-y-auto px-3.5 py-3 text-xs text-slate-700 dark:text-slate-200">
                <section>
                  <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('pages.assistant.helpWhatTitle')}
                  </h3>
                  <p className="leading-relaxed text-slate-600 dark:text-slate-300">
                    {t('pages.assistant.helpWhatBody')}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-slate-600 dark:text-slate-300">
                    {USE_KEYS.map((key) => (
                      <li key={key}>{t(`pages.assistant.${key}`)}</li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('pages.assistant.helpExamplesTitle')}
                  </h3>
                  <ul className="space-y-1.5">
                    {EXAMPLE_KEYS.map((key) => (
                      <li
                        key={key}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200"
                      >
                        {t(`pages.assistant.${key}`)}
                      </li>
                    ))}
                  </ul>
                </section>
                <p className="text-[11px] text-slate-400">{t('pages.assistant.helpNote')}</p>
                <section className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800/60">
                  <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                    {t('pages.assistant.footerHint')}
                  </p>
                  <p className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                    {t('pages.assistant.auditNotice')}
                  </p>
                </section>
                <button
                  type="button"
                  onClick={() => setHelpOpen(false)}
                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                >
                  {t('pages.assistant.helpBack')}
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
                  {messages.length === 0 && !sending && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('pages.assistant.emptyHint')}
                    </p>
                  )}

                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {m.role === 'assistant' && <CoofitoAvatar className="h-6 w-6" />}
                      <div
                        className={`group relative max-w-[85%] ${
                          m.role === 'assistant' ? 'pr-7' : ''
                        }`}
                      >
                        <div
                          className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                            m.role === 'user'
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-100 text-slate-800 dark:bg-gray-800 dark:text-slate-100'
                          }`}
                        >
                          {m.content}
                        </div>
                        {m.role === 'assistant' && (
                          <button
                            type="button"
                            onClick={() => void copyMessage(m.id, m.content)}
                            className="absolute bottom-1 right-0 rounded-md p-1 text-slate-400 opacity-100 transition hover:bg-slate-200 hover:text-slate-700 sm:opacity-0 sm:group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-slate-200"
                            aria-label={
                              copiedId === m.id
                                ? t('pages.assistant.copied')
                                : t('pages.assistant.copy')
                            }
                            title={
                              copiedId === m.id
                                ? t('pages.assistant.copied')
                                : t('pages.assistant.copy')
                            }
                          >
                            {copiedId === m.id ? (
                              <svg className="h-3.5 w-3.5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {sending && (
                    <div className="flex items-end justify-start gap-2">
                      <CoofitoAvatar className="h-6 w-6" />
                      <div className="rounded-2xl bg-slate-100 px-3 py-2 text-xs text-slate-500 dark:bg-gray-800 dark:text-slate-400">
                        {t('pages.assistant.thinking')}
                      </div>
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>

                {sendError && (
                  <div className="border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
                    {sendError}
                  </div>
                )}

                <form
                  onSubmit={onSubmit}
                  className="flex-shrink-0 border-t border-slate-200 p-2.5 dark:border-gray-800"
                >
                  <div className="flex gap-2">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      rows={2}
                      disabled={disabled}
                      placeholder={t('pages.assistant.placeholder')}
                      className="min-h-[2.5rem] flex-1 resize-none rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-none ring-teal-500/40 placeholder:text-slate-400 focus:ring-2 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-950 dark:text-white"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={disabled || !input.trim()}
                      className="self-end"
                    >
                      {sending ? t('pages.assistant.sending') : t('pages.assistant.send')}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex w-full min-h-11 items-center rounded-full bg-teal-600 text-sm font-medium text-white shadow-lg shadow-teal-900/30 transition hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${
          collapsed ? 'justify-center p-1.5' : 'gap-2.5 py-1.5 pl-1.5 pr-3'
        }`}
        aria-expanded={open}
        aria-label={open ? t('pages.assistant.close') : t('pages.assistant.open')}
        title={open ? t('pages.assistant.close') : t('pages.assistant.open')}
      >
        {open ? (
          <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/15">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        ) : (
          <img
            src={COOFITO_ICON}
            alt=""
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-white/40"
            draggable={false}
          />
        )}
        {!collapsed && <span className="truncate pr-0.5">{t('pages.assistant.title')}</span>}
      </button>
    </div>
  );
};
